import type { VariablesOf } from "@graphql-typed-document-node/core";
import type { ValidationErrors } from "final-form";
import { type JSX, useCallback } from "react";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { createSelector } from "reselect";
import { useMutation } from "urql";
import { useAlerts, useAPIAlert } from "../../shared/components/Alerts";
import parseEmails from "../../shared/parseEmails";
import t from "../../shared/translations";
import type { Language } from "../../shared/types";
import { graphql } from "../gql";
import Input from "../Input";
import { closeModal, type IDispatch, type IState } from "../state";
import Textarea from "../Textarea";
import type { IBuilderParams } from "./BuilderRoutes";

const mutation = graphql(`
    mutation BuilderPathSaveMutation($data: BuilderPathSaveData!) {
        builderPathSave(data: $data) {
            error
            builderPath {
                id
            }
        }
    }
`);

interface IFormValues {
    email: string;
    organisation: string;
    note: string;
    subject: string;
}

const pathSelector = createSelector(
    (state: IState) => state.builder.name,
    (state: IState) => state.builder.showThemes,
    (state: IState) => state.builder.showMiles,
    (state: IState) => state.builder.showLength,
    (state: IState) => state.builder.showPrice,
    (state: IState) => state.builder.tasks,
    (name, showThemes, showMiles, showLength, showPrice, tasks) => ({
        name,
        showThemes,
        showMiles,
        showLength,
        showPrice,
        tasks: tasks.map((task) => task.id),
    }),
);

function validate(values: IFormValues): ValidationErrors {
    const errors: ValidationErrors = {};
    if (values.email) {
        if (!values.email.includes("@") || parseEmails(values.email).length === 0) {
            errors.email = t("public.builder.form-save-error-email-invalid");
        }
    }
    return errors;
}

function SaveModal(): JSX.Element {
    const dispatch = useDispatch<IDispatch>();
    const pathData = useSelector(pathSelector);
    const [_, executeMutation] = useMutation(mutation);
    const APIAlert = useAPIAlert();
    const alert = useAlerts();
    const { lang } = useParams<IBuilderParams>();

    const submit = useCallback(
        async (values: IFormValues) => {
            const variables: VariablesOf<typeof mutation> = {
                data: {
                    ...values,
                    ...pathData,
                    locale: lang as Language,
                },
            };
            const result = await executeMutation(variables);
            const success = APIAlert(result, "public.alert.builder-path-failed-to-save");
            if (success) {
                dispatch(closeModal());
                alert(t("public.alert.builder-path-save-succesfull"), "success");
            }
        },
        [executeMutation, pathData, lang],
    );

    return (
        <>
            <h2>{t("public.builder.heading-save-path")}</h2>
            <p>{t("public.builder.info-save-path")}</p>
            <Form<IFormValues> onSubmit={submit} validate={validate}>
                {({ handleSubmit, submitting, values }) => (
                    <form onSubmit={handleSubmit}>
                        <Input name="email" label={t("public.builder.form-save-label-email")} />
                        {!!values.email && values.email.includes("@") && (
                            <Input name="subject" label={t("public.builder.form-save-label-subject")} />
                        )}
                        <Input name="organisation" label={t("public.builder.form-save-label-organisation")} />
                        <Textarea name="note" label={t("public.builder.form-save-label-note")} />
                        <button type="submit" disabled={submitting}>
                            {t("public.builder.form-save-button-submit")}
                        </button>
                    </form>
                )}
            </Form>
        </>
    );
}

export default SaveModal;
